import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Calendar, Mail, MapPin, ChevronDown, Facebook, Instagram, Youtube, Search, Wrench, Zap, Car, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import cargurooLogo from '@/assets/carguroo-circular-logo.png';
const services = [{
  name: 'Purchase Inspection',
  href: '/services/pre-purchase-inspection',
  icon: Search
}, {
  name: 'Car Servicing',
  href: '/services/car-servicing',
  icon: Wrench
}, {
  name: 'Diagnostics',
  href: '/services/diagnostics',
  icon: Zap
}, {
  name: 'Mobile Repairs',
  href: '/services/mobile-repairs',
  icon: Car
}];
export const ModernNav = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [activeSection, setActiveSection] = useState('home');
  const [sectionPositions, setSectionPositions] = useState<{
    [key: string]: {
      top: number;
      height: number;
    };
  }>({});
  const location = useLocation();

  // Cache section positions to avoid forced reflows - defer to next frame
  useEffect(() => {
    let rafId: number;
    let resizeTimeout: NodeJS.Timeout;
    const updateSectionPositions = () => {
      // Defer layout reads to next frame to avoid forced reflows
      rafId = requestAnimationFrame(() => {
        if (location.pathname === '/') {
          const sections = ['home', 'services', 'about', 'areas', 'contact'];
          const positions: {
            [key: string]: {
              top: number;
              height: number;
            };
          } = {};
          sections.forEach(section => {
            const element = document.getElementById(section);
            if (element) {
              positions[section] = {
                top: element.offsetTop,
                height: element.offsetHeight
              };
            }
          });
          setSectionPositions(positions);
        }
      });
    };

    // Debounce resize to avoid excessive layout calculations
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateSectionPositions, 150);
    };

    // Initial update after paint
    updateSectionPositions();
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [location.pathname]);
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          setIsVisible(scrollPosition < 100);
          if (location.pathname === '/' && Object.keys(sectionPositions).length > 0) {
            const scrollPos = scrollPosition + 100;
            const sections = ['home', 'services', 'about', 'areas', 'contact'];
            for (const section of sections) {
              const position = sectionPositions[section];
              if (position && scrollPos >= position.top && scrollPos < position.top + position.height) {
                setActiveSection(section);
                break;
              }
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, sectionPositions]);
  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }

    // Use requestAnimationFrame to prevent forced reflows
    requestAnimationFrame(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    });
    setIsMobileMenuOpen(false);
  };
  const isHomePage = location.pathname === '/';
  return <>
      {/* Top Contact Bar - Flush to top */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-primary text-white py-1.5">
        <div className="max-w-7xl mx-auto px-4 flex justify-center items-center text-xs gap-2 lg:gap-8">
          <div className="flex items-center space-x-2 lg:space-x-6">
            <div className="flex items-center space-x-1">
              <Phone className="w-3 h-3 lg:w-4 lg:h-4" />
              <span>+61 440 134 765</span>
            </div>
            <div className="flex items-center space-x-1">
              <Mail className="w-3 h-3 lg:w-4 lg:h-4" />
              <span className="hidden sm:inline">info@carguroo.com.au</span>
            </div>
            
            <div className="hidden lg:flex items-center space-x-4 ml-8">
              <Facebook className="w-4 h-4 hover:text-primary-foreground cursor-pointer transition-colors" onClick={() => window.open('https://www.facebook.com/carguroomobilemechanic/', '_blank')} />
              <Instagram className="w-4 h-4 hover:text-primary-foreground cursor-pointer transition-colors" onClick={() => window.open('https://www.instagram.com/carguroo/', '_blank')} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation - Below red strip */}
      <nav className="fixed top-[30px] left-0 right-0 z-40 bg-white/95 backdrop-blur-lg shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 lg:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              
              <div className="flex flex-col">
                <span className="text-xl lg:text-2xl font-bold text-primary tracking-tight">CARGUROO</span>
                <span className="text-[10px] lg:text-xs text-muted-foreground font-medium -mt-1">Mobile Mechanic Services</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-evenly flex-1 mx-8">
              <Link to="/" className={`text-base font-semibold transition-colors duration-300 ${isHomePage && activeSection === 'home' ? 'text-primary' : 'text-gray-700 hover:text-primary'}`}>
                HOME
              </Link>
              
              {/* Services Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center text-base font-semibold text-gray-700 hover:text-primary transition-colors duration-300">
                  SERVICES
                  <ChevronDown className="w-4 h-4 ml-1" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="bg-white/95 backdrop-blur-lg border border-gray-200 shadow-xl">
                  {services.map(service => <DropdownMenuItem key={service.name} asChild>
                      <Link to={service.href} className="flex items-center px-4 py-2 text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors duration-300">
                        <service.icon className="w-4 h-4 mr-2" />
                        {service.name}
                      </Link>
                    </DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/about" className="text-base font-semibold text-gray-700 hover:text-primary transition-colors duration-300">
                ABOUT US
              </Link>

              <Link to="/service-areas" className="text-base font-semibold text-gray-700 hover:text-primary transition-colors duration-300">
                AREAS
              </Link>

              <Link to="/blog" className="text-base font-semibold text-gray-700 hover:text-primary transition-colors duration-300">
                BLOG
              </Link>

              <Link to="/contact" className="text-base font-semibold text-gray-700 hover:text-primary transition-colors duration-300">
                CONTACT
              </Link>
            </div>

            {/* Desktop CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <Link to="/get-quote">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 font-semibold text-sm">
                  <FileText className="w-4 h-4 mr-2" />
                  GET A QUOTE
                </Button>
              </Link>
              <Link to="/book-now">
                <Button className="bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg text-sm">
                  <Calendar className="w-4 h-4 mr-2" />
                  BOOK NOW
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-gray-700 hover:text-primary transition-colors duration-300" aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>


        {/* Mobile Menu */}
        {isMobileMenuOpen && <div className="lg:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-xl">
            <div className="px-4 py-6 space-y-4">
              
              {/* Mobile Contact Info */}
              <div className="border-b border-gray-200 pb-4 mb-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                  <Phone className="w-4 h-4" />
                  <span>+61 440 134 765</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>Service Areas</span>
                </div>
              </div>

              {/* Mobile Navigation Links */}
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300 font-semibold">
                HOME
              </Link>

              
              <div className="px-4 py-2">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Services</div>
                <div className="space-y-1">
                  {services.map(service => <Link key={service.name} to={service.href} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center px-2 py-2 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300">
                      <service.icon className="w-4 h-4 mr-2" />
                      {service.name}
                    </Link>)}
                </div>
              </div>

              <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300 font-semibold">
                ABOUT US
              </Link>

              <Link to="/service-areas" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300 font-semibold">
                AREAS
              </Link>

              <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300 font-semibold">
                BLOG
              </Link>

              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300 font-semibold">
                CONTACT
              </Link>

              {/* Mobile CTA Buttons */}
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <Link to="/get-quote" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 font-semibold">
                    <FileText className="w-4 h-4 mr-2" />
                    GET A QUOTE
                  </Button>
                </Link>
                <Link to="/book-now" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg">
                    <Calendar className="w-4 h-4 mr-2" />
                    BOOK NOW
                  </Button>
                </Link>
              </div>

              {/* Mobile Social Links */}
              <div className="flex items-center justify-center space-x-6 pt-4 border-t border-gray-200">
                <Facebook className="w-5 h-5 text-gray-400 hover:text-primary cursor-pointer transition-colors" />
                <Instagram className="w-5 h-5 text-gray-400 hover:text-primary cursor-pointer transition-colors" />
                <Youtube className="w-5 h-5 text-gray-400 hover:text-primary cursor-pointer transition-colors" />
              </div>
            </div>
          </div>}
      </nav>
    </>;
};